/**
 * v-testid directive
 *
 * Adds data-testid (and optionally data-testid-key) attributes to the element
 * in non-production environments. In production, no attributes are added.
 *
 * Usage (string — existing simple form):
 *   <div v-testid="'login-form'">...</div>
 *   <button v-testid="'login-submit-button'">Submit</button>
 *
 * Usage (object — with key for repeated items):
 *   <div v-testid="{ id: 'subscription-panel', key: 'membership-0' }">...</div>
 *   <div v-testid="{ id: 'order-basket-pet-section', key: pet.id }">...</div>
 *
 * The directive respects the centralized environment policy from testIdPolicy.js.
 * Both data-testid and data-testid-key are stripped in production.
 */

import { isTestIdEnabled } from '../utils/testIdPolicy';

/**
 * Vue does not guarantee that the node handed to a directive hook is an Element.
 * A `v-if`/`:show` branch that renders nothing leaves a comment placeholder, and
 * teardown still invokes `unbind` against it. Calling setAttribute/removeAttribute
 * on such a node throws; Vue catches it and downgrades it to a console warning,
 * so the failure is silent in the UI but noisy in error reporting.
 */
function isElementNode(el) {
  return Boolean(el) && typeof el.setAttribute === 'function' && typeof el.removeAttribute === 'function';
}

function applyTestId(el, binding) {
  if (!isTestIdEnabled || !binding.value || !isElementNode(el)) {
    return;
  }

  if (typeof binding.value === 'object') {
    const { id, key } = binding.value;
    if (id) {
      el.setAttribute('data-testid', id);
    }
    if (key != null && key !== '') {
      el.setAttribute('data-testid-key', String(key));
    }
  } else {
    el.setAttribute('data-testid', binding.value);
  }
}

function removeTestId(el) {
  if (!isTestIdEnabled || !isElementNode(el)) {
    return;
  }

  el.removeAttribute('data-testid');
  el.removeAttribute('data-testid-key');
}

export const testIdDirective = {
  bind: applyTestId,
  update(el, binding) {
    if (binding.value !== binding.oldValue) {
      // Always remove both to handle transitions between string/object forms
      removeTestId(el);
      if (binding.value) {
        applyTestId(el, binding);
      }
    }
  },
  unbind: removeTestId,
};

/**
 * Installs the v-testid directive globally on a Vue instance.
 *
 * @param {import('vue').VueConstructor} Vue - The Vue constructor
 */
export function installTestIdDirective(Vue) {
  Vue.directive('testid', testIdDirective);
}
